import { useState, useEffect } from "react";
import { Product, Category } from "@/lib/supabase";
import ImageUploader from "./ImageUploader";
import PurchaseOptionsEditor, { PurchaseOption } from "./PurchaseOptionsEditor";

interface ProductModalProps {
  product: Product | null;
  categories: Category[];
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
  onSubmit: (form: Omit<Product, "created_at">) => void;
}

const BADGE_OPTIONS = ["", "Best Seller", "New", "Trending", "Premium", "Sale"];

const emptyForm = {
  id: "",
  title: "",
  description: "",
  long_description: "",
  highlights: [] as string[],
  price: "",
  discounted_price: "",
  image: "",
  images: [] as string[],
  category: "",
  badge: "",
  is_featured: false,
  purchase_options: [] as PurchaseOption[],
};

const ProductModal = ({ product, categories, onClose, saving, onSubmit }: ProductModalProps) => {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (product) {
      setForm({
        id: product.id,
        title: product.title,
        description: product.description,
        long_description: product.long_description || "",
        highlights: product.highlights || [],
        price: product.price,
        discounted_price: product.discounted_price || "",
        image: product.image,
        images: product.images || [],
        category: product.category,
        badge: product.badge || "",
        is_featured: product.is_featured,
        purchase_options: (product.purchase_options || []) as PurchaseOption[],
      });
    } else {
      setForm(emptyForm);
    }
  }, [product]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImagesChange = (newImages: string[]) => {
    setForm((prev) => ({
      ...prev,
      images: newImages,
      image: newImages[0] || prev.image,
    }));
  };

  const handleHighlightChange = (idx: number, value: string) => {
    setForm((prev) => {
      const arr = [...prev.highlights];
      arr[idx] = value;
      return { ...prev, highlights: arr };
    });
  };

  const addHighlight = () => {
    setForm((prev) => ({ ...prev, highlights: [...prev.highlights, ""] }));
  };

  const removeHighlight = (idx: number) => {
    setForm((prev) => ({ ...prev, highlights: prev.highlights.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...form,
      image: form.images[0] || form.image,
      badge: form.badge || null,
      discounted_price: form.discounted_price || null,
      long_description: form.long_description || null,
      highlights: form.highlights.filter(Boolean).length > 0 ? form.highlights.filter(Boolean) : null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto z-10">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10 rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              {product ? "Edit Product" : "Add New Product"}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {product ? "Update product details below" : "Fill in the details to add a product"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-gray-500 text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Product Title *</label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              required
              placeholder="e.g. Aromatic Soy Candle Set"
              className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description *</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              required
              rows={3}
              placeholder="Short product description..."
              className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all resize-none"
            />
          </div>

          {/* Long Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Full Description
              <span className="text-gray-400 font-normal ml-1 text-xs">shown in product detail popup</span>
            </label>
            <textarea
              name="long_description"
              value={form.long_description}
              onChange={handleChange}
              rows={4}
              placeholder="Detailed product description, materials, dimensions, gifting ideas..."
              className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all resize-none"
            />
          </div>

          {/* Highlights */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Highlights / Key Features
              <span className="text-gray-400 font-normal ml-1 text-xs">bullet points shown in popup</span>
            </label>
            <div className="flex flex-col gap-2">
              {form.highlights.map((h, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-gradient-to-br from-[#3a8cbf] to-[#e05a8a] flex-shrink-0">
                    <i className="ri-check-line text-white text-xs" />
                  </span>
                  <input
                    type="text"
                    value={h}
                    onChange={(e) => handleHighlightChange(i, e.target.value)}
                    placeholder={`Highlight ${i + 1}...`}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => removeHighlight(i)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addHighlight}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-sm text-gray-400 hover:border-[#3a8cbf]/40 hover:text-[#3a8cbf] transition-colors cursor-pointer whitespace-nowrap w-full"
              >
                <i className="ri-add-line" /> Add Highlight
              </button>
            </div>
          </div>

          {/* Price fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Original Price (₹) *
                <span className="text-gray-400 font-normal ml-1 text-xs">MRP / full price</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm">₹</span>
                <input
                  type="number"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  required
                  min="0"
                  placeholder="1599"
                  className="w-full pl-8 pr-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Discounted Price (₹)
                <span className="text-gray-400 font-normal ml-1 text-xs">leave blank if no discount</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#e05a8a] font-medium text-sm">₹</span>
                <input
                  type="number"
                  name="discounted_price"
                  value={form.discounted_price}
                  onChange={handleChange}
                  min="0"
                  placeholder="1299"
                  className="w-full pl-8 pr-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#e05a8a] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all"
                />
              </div>
              {form.discounted_price && form.price && Number(form.discounted_price) < Number(form.price) && (
                <p className="text-xs text-green-600 mt-1.5 font-medium">
                  <i className="ri-price-tag-3-line mr-1" />
                  {Math.round((1 - Number(form.discounted_price) / Number(form.price)) * 100)}% off
                </p>
              )}
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Category *</label>
            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 outline-none transition-all cursor-pointer"
            >
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Badge */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Badge Label</label>
            <select
              name="badge"
              value={form.badge}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 outline-none transition-all cursor-pointer"
            >
              {BADGE_OPTIONS.map((b) => (
                <option key={b} value={b}>{b === "" ? "No Badge" : b}</option>
              ))}
            </select>
          </div>

          {/* Images Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Product Images
              <span className="text-gray-400 font-normal ml-1">(multiple supported)</span>
            </label>
            <ImageUploader images={form.images} onChange={handleImagesChange} />

            {/* Fallback URL if no uploads */}
            {form.images.length === 0 && (
              <div className="mt-3">
                <label className="block text-xs text-gray-500 mb-1.5">Or paste an image URL</label>
                <input
                  type="url"
                  name="image"
                  value={form.image}
                  onChange={handleChange}
                  placeholder="https://..."
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all"
                />
                {form.image && (
                  <div className="mt-2 w-20 h-20 rounded-xl overflow-hidden border border-gray-100">
                    <img src={form.image} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Purchase Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Purchase Options
              <span className="text-gray-400 font-normal ml-1">(where customers can buy)</span>
            </label>
            <PurchaseOptionsEditor
              options={form.purchase_options}
              onChange={(opts) => setForm((prev) => ({ ...prev, purchase_options: opts }))}
            />
          </div>

          {/* Featured toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-[#3a8cbf]/5 to-[#e05a8a]/5 border border-[#3a8cbf]/10">
            <div>
              <p className="text-sm font-semibold text-gray-900">Featured Product</p>
              <p className="text-xs text-gray-500 mt-0.5">Featured products appear on the homepage</p>
            </div>
            <button
              type="button"
              onClick={() => setForm((prev) => ({ ...prev, is_featured: !prev.is_featured }))}
              className={`relative w-12 h-6 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0 ${
                form.is_featured ? "bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]" : "bg-gray-200"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-300 ${
                  form.is_featured ? "translate-x-6" : "translate-x-0"
                }`}
              />
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white text-sm font-semibold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {saving ? "Saving..." : product ? "Save Changes" : "Add Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductModal;
